import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { WorkspaceProvider } from "@/contexts/WorkspaceContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import { Toaster } from "sonner";
import "@/App.css";
import "@/index.css";

const Login = lazy(() => import("@/pages/Login"));
const Register = lazy(() => import("@/pages/Register"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Webhooks = lazy(() => import("@/pages/Webhooks"));
const WebhookDetail = lazy(() => import("@/pages/WebhookDetail"));
const ApiLibrary = lazy(() => import("@/pages/ApiLibrary"));
const Vault = lazy(() => import("@/pages/Vault"));
const ReferenceTables = lazy(() => import("@/pages/ReferenceTables"));
const TestRig = lazy(() => import("@/pages/TestRig"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const AuditLogs = lazy(() => import("@/pages/AuditLogs"));
const Workspaces = lazy(() => import("@/pages/Workspaces"));
const AdminPanel = lazy(() => import("@/pages/AdminPanel"));
const Settings = lazy(() => import("@/pages/Settings"));
const MailboxMonitor = lazy(() => import("@/pages/MailboxMonitor"));
const ComplexWorkflows = lazy(() => import("@/pages/ComplexWorkflows"));
const WorkflowDetail = lazy(() => import("@/pages/WorkflowDetail"));
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const Playground = lazy(() => import("@/pages/Playground"));
const Blueprints = lazy(() => import("@/pages/Blueprints"));
const Subscription = lazy(() => import("@/pages/Subscription"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <WorkspaceProvider>
          <Toaster position="top-right" richColors closeButton />
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen bg-[#F3F0EA]"><div className="w-5 h-5 border-2 border-[#0A1128]/20 border-t-[#0A1128] rounded-full animate-spin" /></div>}>
            <Routes>
              {/* Public routes */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />

              {/* Protected app routes — all under /app */}
              <Route path="/app" element={<ProtectedRoute><Layout /></ProtectedRoute>}>
                <Route index element={<Navigate to="dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="webhooks" element={<Webhooks />} />
                <Route path="webhooks/:id" element={<WebhookDetail />} />
                <Route path="api-library" element={<ApiLibrary />} />
                <Route path="vault" element={<Vault />} />
                <Route path="reference-tables" element={<ReferenceTables />} />
                <Route path="test-rig" element={<TestRig />} />
                <Route path="analytics" element={<Analytics />} />
                <Route path="audit-logs" element={<AuditLogs />} />
                <Route path="workspaces" element={<Workspaces />} />
                <Route path="settings" element={<Settings />} />
                <Route path="admin" element={<AdminPanel />} />
                <Route path="mailbox-monitor" element={<MailboxMonitor />} />
                <Route path="complex-workflows" element={<ComplexWorkflows />} />
                <Route path="complex-workflows/:id" element={<WorkflowDetail />} />
                <Route path="playground" element={<Playground />} />
                <Route path="blueprints" element={<Blueprints />} />
                <Route path="subscription" element={<Subscription />} />
              </Route>

              {/* Catch-all — redirect old paths */}
              <Route path="/dashboard" element={<Navigate to="/app/dashboard" replace />} />
              <Route path="/webhooks" element={<Navigate to="/app/webhooks" replace />} />
              <Route path="/api-library" element={<Navigate to="/app/api-library" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </WorkspaceProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
